import { Box, Grid, makeStyles } from '@material-ui/core';
import { GeneralCatalogItem } from 'data-layer-cr/dist/models';
import { useNetworkObserver } from 'data-layer-cr/dist/network';
import {
  DataSourceProvider,
  DisplayItem,
  FormHookResult,
  NetworkIndicator,
  SearchableDropDown,
  colors,
} from 'design-system';

import { makeDefaultMoneyField } from 'src/components/MakeFormFields';

// Network
import { useState } from 'react';
import { GET_BANKS } from '../../../../../store/creditorCatalog/requestIdentifiers';

import { withBanksValues } from '../../../../../constants/creditorTypes';

type Props = {
  bankList: GeneralCatalogItem[];
  dataSource: DataSourceProvider;
  isDisabled: boolean;
  noMaxHeight?: boolean;
  getInputProps: FormHookResult;
  currencySelected?: string;
  insuranceValue: number;
};

export const getMoneyFieldValue = (
  insuranceValue: number,
  currentValue: number,
  bank: string,
): string => {
  if (bank === '') {
    return '';
  }

  return currentValue ? currentValue.toString() : insuranceValue.toString();
};

export const setMoneyFieldDisable = (bank: string): boolean => bank === '';

export const setNumericValue = (numberSelected: string): number => {
  return numberSelected === '' ? NaN : Number(numberSelected);
};

const useStyles = makeStyles({
  title: {
    fontSize: 17,
    fontWeight: 700,
    lineHeight: '25px',
    color: colors.madison,
  },
});

const CreditorCard = ({
  bankList,
  dataSource,
  isDisabled,
  getInputProps,
  currencySelected = 'USD',
  insuranceValue,
}: Props): JSX.Element => {
  const classes = useStyles();
  const requestBanksStatus = useNetworkObserver(GET_BANKS);

  const formData = dataSource<string>();

  // Extract creditor type form data
  const creditorType = formData('creditorType').value;

  // Extract bank form data
  const currentBank = formData('currentBank').value;
  const updateCurrentBank = formData('currentBank').onChange;
  const currentCreditAmount = setNumericValue(formData('creditAmount').value);
  const updateCurrentCreditAmount = formData('creditAmount').onChange;
  const bankError = formData('currentBank').error;
  const bankErrorMessage = formData('currentBank').errorText;

  const [disableCreditAmount, setDisableCreditAmount] = useState(
    currentBank === '',
  );

  const handleChangeCreditor = (newValue: string) => {
    updateCurrentCreditAmount(
      getMoneyFieldValue(insuranceValue, currentCreditAmount, newValue),
    );
    setDisableCreditAmount(setMoneyFieldDisable(newValue));
    updateCurrentBank(newValue);
  };

  /**
   * Banks are GeneralCatalogItems, but we need them to be DisplayItems
   * to use them as options in the dropdown
   */
  const bankDisplayItems = [...bankList]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(
      (bank): DisplayItem => ({
        Value: bank.code,
        Text: bank.name,
      }),
    );

  return (
    <Grid data-testid="creditor-cards">
      <Grid container spacing={3}>
        {withBanksValues.includes(creditorType) && (
          <Grid item xs={12}>
            <NetworkIndicator
              requesting={requestBanksStatus === 'requesting'}
              variant="horizontal"
              message="Obteniendo acreedores..."
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <Box display="flex" sx={{ width: '100%' }}>
            <Box>
              <span className={classes.title}>
                Tu crédito puede ser respaldado, selecciona tu acreedor
              </span>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={8}>
          {requestBanksStatus !== 'requesting' && (
            <SearchableDropDown
              inputId="sales-web-inspection-dropdown-creditor"
              options={bankDisplayItems}
              inputName="currentBank"
              defaultValue={currentBank}
              inputError={bankError}
              inputErrorMessage={bankErrorMessage}
              onChange={(newVal): void => handleChangeCreditor(newVal.Value)}
              isDisabled={isDisabled}
              inputLabel="Acreedores registrados"
              placeholder="Selecciona un acreedor"
            />
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          {makeDefaultMoneyField({
            inputId: 'sales-web-inspection-credit',
            label: 'Acreencia',
            getInputProps,
            inputName: 'creditAmount',
            currencyType: currencySelected,
            isDisabled: disableCreditAmount,
            defaultValue: currentCreditAmount,
          })}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default CreditorCard;
